import { useUpdateRouteMutation } from '@/store/api/services/operations/routes/index';
import { useGetUsersAthenaQuery } from '@/store/api/services/users/users';
import type { ModalChildrenProps } from '@bitstopco/bitstop-theme';
import { zodResolver } from '@hookform/resolvers/zod';
import { LoadingButton } from '@mui/lab';
import {
  Button,
  DialogActions,
  DialogContent,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  TextareaAutosize,
  Typography,
} from '@mui/material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DesktopDateTimePicker } from '@mui/x-date-pickers/DesktopDateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import { z } from 'zod';

import type { Route } from '@/types/route';

const RouteEditSchema = z.object({
  name: z.string().optional(),
  technician_id: z.string().optional(),
  scheduled_at: z.date().nullable().optional(),
  route_status: z.string().min(1, 'Route status is required'),
  notes: z.string().optional(),
});

export type RouteEditFormData = z.infer<typeof RouteEditSchema>;

export interface UpdateRoutePayload {
  id: string;
  route_status: string;
  technician_id: string | null;
  name: string | null;
  scheduled_at: string | null;
  notes: string | null;
}

// DEV-2134: Boro's UpdateRoute can't currently tell "field omitted" apart from "explicitly
// cleared" for technician_id - both deserialize to null and are silently skipped, so sending
// `null` here does NOT unassign an existing technician yet. Once that ticket lands on a wire
// format (e.g. a dedicated clear_technician flag), this payload shape needs to be updated to
// match - don't assume "send null" is still correct without checking DEV-2134's resolution.
export const buildUpdateRoutePayload = (
  data: RouteEditFormData,
  routeId: string,
): UpdateRoutePayload => ({
  id: routeId,
  route_status: data.route_status,
  technician_id: data.technician_id ? data.technician_id : null,
  name: data.name?.trim() ? data.name.trim() : null,
  scheduled_at: data.scheduled_at ? data.scheduled_at.toISOString() : null,
  notes: data.notes?.trim() ? data.notes.trim() : null,
});

interface RouteEditProps extends ModalChildrenProps {
  props: {
    routeData?: Route;
    onSuccess?: () => void;
  } | null;
}

const RouteEdit = ({ hide, props }: RouteEditProps) => {
  // Early return if props or routeData is null/undefined
  if (!props?.routeData) {
    return null;
  }

  const { routeData, onSuccess } = props;

  const [updateRoute, { isLoading }] = useUpdateRouteMutation();

  // Fetch users for technician dropdown (role_id = 5: Machine Dispatch/Tech)
  const { data: usersData, isLoading: isLoadingUsers } = useGetUsersAthenaQuery({ role_id: 5 });
  const users = usersData?.data || [];

  const {
    control,
    handleSubmit,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<RouteEditFormData>({
    resolver: zodResolver(RouteEditSchema),
    defaultValues: {
      name: routeData?.name || '',
      technician_id: routeData?.technician_id ? String(routeData.technician_id) : '',
      scheduled_at: routeData?.scheduled_at ? new Date(routeData.scheduled_at) : null,
      route_status: routeData?.route_status || 'planning',
      notes: routeData?.notes || '',
    },
  });

  // Update form when routeData changes
  useEffect(() => {
    if (routeData) {
      setValue('name', routeData.name || '');
      setValue('technician_id', routeData.technician_id ? String(routeData.technician_id) : '');
      setValue('scheduled_at', routeData.scheduled_at ? new Date(routeData.scheduled_at) : null);
      setValue('route_status', routeData.route_status || 'planning');
      setValue('notes', routeData.notes || '');
    }
  }, [routeData, setValue]);

  const onSubmit = async (data: RouteEditFormData) => {
    if (!routeData?.id) {
      toast.error('Route ID is required');
      return;
    }

    try {
      const payload = buildUpdateRoutePayload(data, routeData.id);

      await updateRoute(payload).unwrap();

      toast.success('Route updated successfully');
      if (onSuccess) {
        onSuccess();
      }
      hide();
    } catch (error: any) {
      toast.error(error?.data?.message || 'Failed to update route');
      console.error('Error updating route:', error);
    }
  };

  return (
    <>
      <DialogContent sx={{ py: 4 }}>
        <Stack gap={2} component="form" id="route-edit-form" onSubmit={handleSubmit(onSubmit)}>
          <Controller
            name="name"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label="Route Name"
                size="small"
                fullWidth
                error={!!errors.name}
                helperText={errors.name?.message}
              />
            )}
          />

          <FormControl fullWidth size="small">
            <InputLabel>Assigned Technician</InputLabel>
            <Controller
              name="technician_id"
              control={control}
              render={({ field }) => (
                <Select
                  {...field}
                  label="Assigned Technician"
                  displayEmpty
                  disabled={isLoadingUsers}
                  data-test="route-edit-technician-select"
                  MenuProps={{
                    PaperProps: {
                      style: {
                        maxHeight: 300,
                      },
                    },
                  }}
                >
                  <MenuItem value="" data-test="route-edit-technician-empty">
                    No technician assigned
                  </MenuItem>
                  {users.map((user) => (
                    <MenuItem
                      key={user.id}
                      value={user.id.toString()}
                      data-test={`route-edit-technician-option-${user.id}`}
                    >
                      {user.first_name && user.last_name
                        ? `${user.first_name} ${user.last_name}`
                        : user.email || user.id}
                    </MenuItem>
                  ))}
                </Select>
              )}
            />
          </FormControl>

          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <Controller
              name="scheduled_at"
              control={control}
              render={({ field: { value, onChange } }) => (
                <DesktopDateTimePicker
                  label="Scheduled Date & Time"
                  value={value}
                  onChange={onChange}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      size="small"
                      fullWidth
                      error={!!errors.scheduled_at}
                      helperText={errors.scheduled_at?.message}
                    />
                  )}
                />
              )}
            />
          </LocalizationProvider>

          <FormControl fullWidth size="small" error={!!errors.route_status}>
            <InputLabel>Route Status *</InputLabel>
            <Controller
              name="route_status"
              control={control}
              render={({ field }) => (
                <Select {...field} label="Route Status *">
                  <MenuItem value="planning">Planning</MenuItem>
                  <MenuItem value="scheduled">Scheduled</MenuItem>
                  <MenuItem value="in_progress">In Progress</MenuItem>
                  <MenuItem value="completed">Completed</MenuItem>
                  <MenuItem value="cancelled">Cancelled</MenuItem>
                </Select>
              )}
            />
            {errors.route_status && (
              <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>
                {errors.route_status.message}
              </Typography>
            )}
          </FormControl>

          <Stack>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Notes
            </Typography>
            <Controller
              name="notes"
              control={control}
              render={({ field }) => (
                <>
                  <TextareaAutosize
                    {...field}
                    minRows={4}
                    maxRows={8}
                    placeholder="Enter route notes (optional)"
                    style={{
                      width: '100%',
                      resize: 'vertical',
                      padding: '12px 14px',
                      border: errors.notes ? '1px solid #d32f2f' : '1px solid rgba(0, 0, 0, 0.23)',
                      borderRadius: '4px',
                      fontSize: '0.875rem',
                      fontFamily: 'inherit',
                      lineHeight: '1.5',
                      backgroundColor: 'transparent',
                      outline: 'none',
                      cursor: 'text',
                    }}
                  />
                  {errors.notes && (
                    <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
                      {errors.notes.message}
                    </Typography>
                  )}
                </>
              )}
            />
          </Stack>
        </Stack>
      </DialogContent>

      <Divider />

      <DialogActions sx={{ justifyContent: 'space-between', p: 2 }}>
        <Button onClick={hide} disabled={isSubmitting || isLoading}>
          Discard changes
        </Button>
        <LoadingButton
          variant="contained"
          disabled={isSubmitting || isLoading}
          loading={isSubmitting || isLoading}
          onClick={handleSubmit(onSubmit)}
        >
          Save Changes
        </LoadingButton>
      </DialogActions>
    </>
  );
};

export default RouteEdit;
